import { useEffect, useState } from "react";
import { subtareasApi } from "src/services/subtareasApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { useSnackbar } from "src/components/snackbar";

// Estados en que la tarea ya no admite seleccionar ni eliminar subtareas.
const ESTADOS_TAREA_CERRADA = [
  "TERMINADA",
  "FACTURADO",
  "PENDIENTE DE FACTURACION",
];

// Una subtarea terminada no se puede eliminar: el backend la rechaza con 409 en la ruta
// individual y la omite en el lote, así que tampoco se ofrece seleccionarla.
const ESTADO_SUBTAREA_TERMINADA = "TERMINADA";

/**
 * Selección múltiple y eliminación en lote de subtareas. Lo usan las dos vistas que
 * listan subtareas de una tarea: `Subtareas.js` (pestaña del detalle) y
 * `SubTareasDialog.js` (diálogo desde Tareas e Inicio).
 *
 * `columnas` es el TABLE_HEAD de la tabla que lo llama; se devuelve como `headLabel`
 * ajustado según si la casilla del encabezado se muestra o no.
 */
export default function useSeleccionSubtareas({ tarea, subtareas, columnas }) {
  const [subTareasSeleccionadas, setSubTareasSeleccionadas] = useState([]);
  const [eliminarSubtareasLote] = subtareasApi.useDeleteSubtareasLoteMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Una tarea cerrada no admite seleccionar, editar ni eliminar subtareas. Única fuente
  // de la regla: la usan el encabezado, la barra de acciones y, vía
  // `puedeModificarSubtarea`, las acciones de cada fila.
  const tareaCerrada = ESTADOS_TAREA_CERRADA.includes(tarea.estado);
  const puedeSeleccionar = !tareaCerrada;

  // Qué filas admiten acciones: ni las de una tarea cerrada ni las subtareas ya
  // terminadas. Única fuente de la regla para las tres cosas que ofrece cada fila —la
  // casilla, el lápiz y el basurero—, que antes divergían entre las dos vistas.
  const puedeModificarSubtarea = (subtarea) =>
    puedeSeleccionar && subtarea.estado !== ESTADO_SUBTAREA_TERMINADA;

  const subtareasSeleccionables = puedeSeleccionar
    ? subtareas.filter(
        (subtarea) => subtarea.estado !== ESTADO_SUBTAREA_TERMINADA,
      )
    : [];

  // Si la tarea se cierra con la vista abierta, la selección deja de tener sentido:
  // sin esto queda viva y reaparece si la tarea vuelve a un estado editable.
  useEffect(() => {
    if (tareaCerrada) {
      setSubTareasSeleccionadas([]);
    }
  }, [tareaCerrada]);

  // La selección guarda objetos de fila, así que se desincroniza de la lista: una subtarea
  // borrada con el basurero, borrada por otro usuario o pasada a TERMINADA seguiría
  // contada. Con el conteo descuadrado la casilla del encabezado nunca queda `checked` y
  // deja de poder desmarcar. Se poda en cada llegada de datos; devolver el mismo arreglo
  // cuando no cambió nada evita re-renderizar de más.
  useEffect(() => {
    setSubTareasSeleccionadas((actuales) => {
      if (actuales.length === 0) return actuales;

      const vigentes = actuales.filter((seleccionada) =>
        subtareas.some(
          (subtarea) =>
            subtarea.id === seleccionada.id &&
            subtarea.estado !== ESTADO_SUBTAREA_TERMINADA,
        ),
      );

      return vigentes.length === actuales.length ? actuales : vigentes;
    });
  }, [subtareas]);

  // Las filas siempre pintan una primera celda (la de la casilla, o una vacía cuando la
  // tarea está cerrada). Sin casilla en el encabezado hay que compensarla o las columnas
  // quedan corridas.
  const headLabel = puedeSeleccionar
    ? columnas
    : [{ id: "seleccion", label: "", alignRight: false }, ...columnas];

  const handleSelectAllClick = (event) => {
    setSubTareasSeleccionadas(
      event.target.checked ? [...subtareasSeleccionables] : [],
    );
  };

  const estaSeleccionada = (subtarea) =>
    subTareasSeleccionadas.some((e) => e.id === subtarea.id);

  const alternarSeleccion = (subtarea, seleccionar) => {
    setSubTareasSeleccionadas((actuales) =>
      seleccionar
        ? [...actuales, subtarea]
        : actuales.filter((e) => e.id !== subtarea.id),
    );
  };

  const handleDeleteLista = () => {
    const cantidad = subTareasSeleccionadas.length;

    AlertaModalV2({
      title: `¿Eliminar ${cantidad} ${
        cantidad === 1 ? "subtarea" : "subtareas"
      }?`,
      text: `Se eliminará también el tiempo registrado en ${
        cantidad === 1 ? "ella" : "ellas"
      }, sin posibilidad de recuperarlo. ¿Está seguro?`,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    })
      .fire()
      .then(async (result) => {
        if (!result.isConfirmed) return;

        try {
          const resultado = await eliminarSubtareasLote({
            refTarea: tarea.id,
            ids: subTareasSeleccionadas.map((subtarea) => subtarea.id),
          }).unwrap();

          const eliminadas = resultado?.eliminadas ?? 0;
          const idsFallidos = resultado?.fallidas ?? [];

          if (idsFallidos.length === 0) {
            enqueueSnackbar(
              `${eliminadas} ${
                eliminadas === 1 ? "subtarea eliminada" : "subtareas eliminadas"
              }`,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(
              `${eliminadas} eliminadas, ${idsFallidos.length} no se pudieron eliminar`,
              { variant: "warning" },
            );
          }

          // Las que fallaron siguen seleccionadas para poder reintentarlas.
          setSubTareasSeleccionadas(
            subTareasSeleccionadas.filter((subtarea) =>
              idsFallidos.includes(subtarea.id),
            ),
          );
        } catch (error) {
          // El backend relanza sus propios errores con código y mensaje: 403 sin acceso a
          // la sucursal, 404 tarea inexistente, 409 tarea cerrada. Mostrarlo cuando viene;
          // el genérico solo para lo que no lo trae.
          enqueueSnackbar(error?.data?.message || "Error en el servidor", {
            variant: "error",
          });
        }
      });
  };

  return {
    subTareasSeleccionadas,
    puedeSeleccionar,
    // Cuántas filas ofrecen casilla. Es lo que hay que pasarle a `rowCount` de TablaHead:
    // con el total de subtareas, la casilla del encabezado no llegaría nunca a `checked`
    // en una tarea con subtareas terminadas.
    subtareasSeleccionables,
    puedeModificarSubtarea,
    headLabel,
    handleSelectAllClick,
    estaSeleccionada,
    alternarSeleccion,
    handleDeleteLista,
  };
}
